<template>
  <div class="marketing-topics-page-container">
    <div ref="pageRef" v-editable="blok" class="marketing-topics-page">
      <div class="marketing-topics-page__aside">
        <div id="marketing-topics-page-aside" class="marketing-topics-page__aside-sticky"></div>
      </div>

      <div class="marketing-topics-page__content">
        <template v-for="item in blok.body" :key="item._uid">
          <Teleport
            v-if="item.component === 'MarketingParallaxImageBlok'"
            to="#marketing-topics-page-aside"
            :disabled="!isBannerInAside"
          >
            <StoryblokComponent :blok="item" />
          </Teleport>
          <StoryblokComponent v-else :blok="item" />
        </template>
        <BaseDPGFooter :footer-text="t('dpg.footerText', [new Date().getFullYear()], { locale: country })">
          <ul class="marketing-topics-page__footer-links">
            <li>
              <a
                :href="`https://privacy.dpgmedia.${country}/nl/document/privacy-policy`"
                target="_blank"
                rel="nofollow"
              >
                {{ t('marketingPages.footer.privacyPolicy') }}
              </a>
            </li>
            <li>
              <a :href="`https://privacy.dpgmedia.${country}/nl/document/cookie-policy`" target="_blank" rel="nofollow">
                {{ t('marketingPages.footer.cookiePolicy') }}
              </a>
            </li>
            <li>
              <button type="button" @click="openPrivacySettings">
                {{ t('marketingPages.footer.privacySettings') }}
              </button>
            </li>
          </ul>
        </BaseDPGFooter>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  useGtm({
    gtmCode: 'GTM-KDQ73J6R',
  });

  const { blok } = defineProps<{ blok: MarketingTopicsPageStoryblok }>();

  const { t } = useI18n();
  const country = inject<'nl' | 'be'>('topicsCountry', 'nl');
  const seoMeta = blok.seo_metas?.[0];

  const { $gsap } = useNuxtApp();
  const pageRef = useTemplateRef<HTMLDivElement>('pageRef');

  const isDesktop = useMediaQuery('(min-width: 64em)');
  const isMounted = useMounted();
  const isBannerInAside = computed(() => isMounted.value && isDesktop.value);

  useMarketingSeo({
    seoMeta,
  });

  function animateTitleHighlights() {
    if (!pageRef.value || !blok.has_italic_highlight) return;

    const headings = pageRef.value.querySelectorAll<HTMLElement>(
      'h1 :is(i, em), h2 :is(i, em), h3 :is(i, em), h4 :is(i, em), h5 :is(i, em), h6 :is(i, em)',
    );

    headings.forEach((highlight) => {
      // Get the parent heading for ScrollTrigger
      const parentHeading = highlight.closest('h1, h2, h3, h4, h5, h6') as HTMLElement;
      if (!parentHeading) return;

      // Set initial state (fully clipped)
      $gsap.set(highlight, {
        '--clip-progress': '100%',
      });

      // Animate to reveal after 1 second delay when visible
      $gsap.to(highlight, {
        '--clip-progress': '0%',
        duration: 0.8,
        delay: 1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: parentHeading,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });
  }

  function openPrivacySettings() {
    window._privacy?.openModal();
  }

  onMounted(() => {
    // Use nextTick to ensure Storyblok components are rendered
    nextTick(() => {
      animateTitleHighlights();
    });
  });
</script>

<style lang="postcss">
  .marketing-topics-page-container {
    background-color: var(--colors-gray-100);
  }

  .marketing-topics-page {
    display: grid;
    max-width: 440px;
    margin: 0 auto;
    background-color: var(--colors-white);

    @media (--screen-lg) {
      grid-template-columns: minmax(0, 592fr) minmax(0, 847fr);
      max-width: none;
    }

    &__aside {
      display: none;

      @media (--screen-lg) {
        display: block;
        background-color: var(--colors-primary-50);
      }
    }

    &__aside-sticky {
      position: sticky;
      top: 0;
    }

    &__footer-links {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-2) var(--spacing-4);
      justify-content: center;

      list-style: none;

      a,
      button {
        color: inherit;
        text-decoration: underline;
      }

      button {
        cursor: pointer;
        font: inherit;
      }
    }

    &__content {
      @media (--screen-lg) {
        padding-inline: var(--spacing-9);

        h1 {
          font-size: 3.5rem;
        }

        .app-text-hero__description {
          max-width: 50ch;
          text-wrap: pretty;
        }
      }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      position: relative;

      :is(i, em) {
        --clip-progress: 100%;

        position: relative;
        z-index: 1;

        &::before {
          content: '';

          position: absolute;
          z-index: -1;
          top: 0;
          left: 0;
          transform: translateX(-2%);

          width: 104%;
          height: 100%;

          background-color: var(--marker-background-color);
          clip-path: inset(0 var(--clip-progress) 0 0);
        }
      }
    }
  }
</style>
